import { Component, OnInit, OnDestroy, Optional, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray, UntypedFormControl, AbstractControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription, throwError, TimeoutError } from 'rxjs';
import moment from 'moment';
import { DateTime } from 'luxon';
import { PhoneNumberUtil, PhoneNumber } from 'google-libphonenumber';
import { MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { SettingsService } from '../services/settings.service';
import { OrderService } from './services/order.service';
import { SnackbarService } from '../services/snackbar.sevice';
import { ClientSite } from '../models/clientsite';
import { User, Org } from '../models/user';
import { CountrySetting } from '../models/countrysetting';
import { Constants } from '../models/constants';
import { ListItem } from '../models/listitem';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { AutocompleteService } from './services/autocomplete.service';
import { MessageBoxService } from '../messagebox/services/messagebox.service';
import { MessageBoxButtons, MessageBoxIcon, MessageBoxResult } from '../messagebox/messagebox.model';
import {
    GetCommonStudyDescription,
    PatientLocationDTO,
    ReferringDoctorDTO,
    RemoteOrderDetails,
    RemoteOrderItem,
    RemoteOrderPrior,
    RemoteOrderRequest,
    StrokeProtocol,
    UrgencyType
} from '../api-module/control-centre-api-client';
import { DatePickerDateAdapter } from './adapters/date-picker-date-adapter';
import { DateAdapter } from '@angular/material/core';
import { FeaturesService } from '../services/features.service';
import { OrgSiteSelectionService } from '../shared/org-site-selection/org-site-selection.service';
import { DropdownOption } from '@app/models/dropdown-option';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';

const phoneUtil = PhoneNumberUtil.getInstance();
const SEPARATOR_IN_NOTES = '\r\n';

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.scss'],
    providers: [{ provide: DateAdapter, useClass: DatePickerDateAdapter }],
    encapsulation: ViewEncapsulation.None
})
export class OrderComponent implements OnInit, OnDestroy {
    constructor(
        private readonly settingsService: SettingsService,
        private readonly authenticationService: AuthenticationService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly orderService: OrderService,
        private readonly snackbarService: SnackbarService,
        private readonly autocompleteService: AutocompleteService,
        private readonly messageBoxService: MessageBoxService,
        private readonly featuresService: FeaturesService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        @Optional() private readonly dialogRef: MatDialogRef<OrderComponent>
    ) {}

    get form() {
        return this.orderForm.controls;
    }

    get studyForms(): UntypedFormArray {
        return this.orderForm.get('studies') as UntypedFormArray;
    }

    get priorForms(): UntypedFormArray {
        return this.orderForm.get('priors') as UntypedFormArray;
    }

    get referringDoctorCountryCode(): string {
        if (this._verbalPhoneCountryCode) {
            return this._verbalPhoneCountryCode;
        } else {
            return this.defaultCountryCode;
        }
    }

    urgencyDropdownOptions = new BehaviorSubject<DropdownOption<UrgencyType | StrokeProtocol>[]>([]);

    private newOrderAdded = false;

    private readonly subscriptions = new Subscription();

    // Phone number related constants
    private readonly PHONE_PATTERN = '[0-9]*';
    private readonly PHONE_EXTENSION_BRIEF = 'ext';

    private readonly MAX_SIZE_PHONE_EXTENSION = 10;

    @ViewChild('dialogContentScrollArea')
    private readonly dialogContentScrollArea: ElementRef;

    defaultCountryCode: string;

    user: User;

    org: Org;
    sites: ClientSite[];
    countrySettings: CountrySetting[];
    commonStudies: GetCommonStudyDescription[];
    urgencyItems = [new ListItem('ST', 'Urgent')];
    protocolTypes = [new ListItem('On Site', 'Completed Onsite'), new ListItem('At Everlight', 'At Everlight')];
    strokeProtocol = StrokeProtocol;
    referringDoctorsSubject = new BehaviorSubject<ReferringDoctorDTO[]>([]);
    patientLocationsSubject = new BehaviorSubject<PatientLocationDTO[]>([]);
    filteredReferringDoctors: Observable<string[]>;
    filteredReferringDoctorPhoneNo: Observable<string[]>;
    filteredReferringDoctorExtension: Observable<string[]>;
    filteredPatientLocation: Observable<string[]>;
    private _verbalPhoneCountryCode: string = null;

    studiesGroups: Map<string, GetCommonStudyDescription[]>;
    modalityList: string[];

    orderForm: UntypedFormGroup;

    saving = false;

    onClosingOrderDialog() {
        if (this.orderForm.dirty) {
            this.dialogRef.disableClose = true;
            const title = 'Please Confirm';
            const message = 'Are you sure you want to close this form?';
            const information = 'You have not saved the order. All details entered on this form will be lost if you close this form now.';

            const msg = this.messageBoxService.showMessageBox({
                title,
                message,
                information,
                icon: MessageBoxIcon.Confirmation,
                buttons: MessageBoxButtons.YesNo
            });
            msg.subscribe(result => {
                if (result === MessageBoxResult.Yes) {
                    this.dialogRef.close(false);
                } else {
                    // do nothing
                }
            });
        } else {
            this.dialogRef.close(false);
        }
    }

    ngOnInit() {
        this.initOrderFrom();

        this.subscribeCurrentUser();

        this.getCountrySettings();
        this.getCommonStudies();

        this.addPrior();

        // This is to make IE11 not to report form dirty after loading.
        setTimeout(() => {
            this.orderForm.markAsPristine();
        }, 50);

        this.pipeVerbalDoctorReceivingReportValueChange();
        this.pipeReferringDoctorExtensionValueChanges();
        this.pipePatientLocationValueChanges();
        this.subscribeSiteIdChange();
        this.initUrgencyDropdownOptions();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    addPrior() {
        this.priorForms.push(this.newPriorForm());
    }

    deletePrior(index: number) {
        if (this.priorForms.length <= 1) {
            return;
        }
        this.priorForms.removeAt(index);
    }

    submit() {
        if (this.orderForm.valid) {
            this.saving = true;
            const order = this.getOrderFromForm();

            // TODO Once API is ready, need proper error handling here.
            this.orderService
                .saveOrder(order)
                .pipe(catchError(error => this.getErrorMessage(error)))
                .subscribe({
                    next: () => {
                        this.snackbarService.openSnackBar(
                            'The order has been successfully created.',
                            SnackbarService.SNACK_BAR_CLASS_SUCCESS
                        );
                        this.notifyWorklistNewOrderFormClosed();
                        this.reset();
                        this.saving = false;
                        this.newOrderAdded = true;
                        this.dialogContentScrollArea.nativeElement.scrollTop = 0;
                    },
                    error: error => {
                        this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        this.saving = false;
                    }
                });
        } else {
            this.markAsTouched(this.orderForm);
            this.snackbarService.openSnackBar(
                'The order cannot be saved. Please resolve the errors.',
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
            this.saving = false;
        }
    }

    getErrorMessage(response: HttpErrorResponse | TimeoutError) {
        if (response instanceof TimeoutError) {
            console.error('Request timeout: ', JSON.stringify(response));
            return throwError(Constants.UnableToContactServerMessage);
        } else if (
            response instanceof HttpErrorResponse &&
            (response.error instanceof ProgressEvent || response.status === 502 || response.status === 503 || response.status === 504)
        ) {
            console.error('Unable to contact server: ', response.status);
            return throwError(Constants.UnableToContactServerMessage);
        }
        console.error(`Server returned code ${response.status} with body:\n${JSON.stringify(response.error)}`);
        if (response.status === 403) {
            return throwError('You do not have permission to create this order');
        }
        if (response.status === 400) {
            // SPA shouldn't submit an invalid form. if it does and server returns error, it's an error scenario.
            const errorMessage =
                'Something went wrong while submitting the order. Please try again in some time. ' +
                'If the problem persists, please contact Everlight support team';
            return throwError(errorMessage);
        }
        return throwError(
            'Order creation failed. Please try again in some time. ' + 'If the problem persists, please contact Everlight support team'
        );
    }

    reset() {
        this.clearStudies();
        this.orderForm.reset({
            urgency: Constants.UrgencyStat
        });
        this.setSiteIfOnlyOne();
        this.setContactDetailsFromUser();

        this.resetPriors();
        this.dialogContentScrollArea.nativeElement.scrollTop = 0;
    }

    notifyWorklistNewOrderFormClosed() {
        if (!!this.dialogRef && !this.form.isToCreateAnotherOrder.value) {
            this.dialogRef.close(this.newOrderAdded);
        }
    }

    onPhoneNumberChanged(phoneNumberControl: AbstractControl, phoneExtControl: AbstractControl) {
        if (!phoneNumberControl.value) {
            phoneExtControl.reset();
            return;
        }
        const phoneNumberNew = (this.defaultCountryCode ? this.defaultCountryCode + ' ' : '') + phoneNumberControl.value;

        let validNumber = false;
        let phoneNumber: PhoneNumber;
        try {
            phoneNumber = phoneUtil.parseAndKeepRawInput(phoneNumberNew, '');
            validNumber = phoneUtil.isValidNumber(phoneNumber);
        } catch (e) {}

        if (validNumber) {
            phoneNumberControl.setValue(phoneNumber.getNationalNumber());
        } else {
            phoneNumberControl.setErrors({ invalidphone: true });
        }
    }

    onPriorsAvailableChanged(priorSentControl: AbstractControl) {
        if (priorSentControl.value) {
            if (this.priorForms.length === 0) {
                this.addPrior();
            }
        } else {
            this.resetPriors();
        }
    }

    onSameAsYourDetailsChanged(sameAsYourDetailsControl: AbstractControl) {
        if (sameAsYourDetailsControl.value) {
            this.form.radiographerName.reset();
            this.form.radiographerPhone.reset();
            this.form.radiographerPhoneExt.reset();
        }
    }

    dateFilter = (date: Date): boolean => date <= new Date();

    private clearStudies() {
        while (this.studyForms.length > 1) {
            this.studyForms.removeAt(this.studyForms.length - 1);
        }
    }

    private resetPriors() {
        // remove all items since they have validators and then add new one with null validators
        while (this.priorForms.length > 0) {
            this.priorForms.removeAt(this.priorForms.length - 1);
        }
        this.addPrior();
    }

    private subscribeCurrentUser() {
        this.subscriptions.add(
            this.authenticationService.currentUser.subscribe(user => {
                if (!user) {
                    return;
                }

                this.user = user;
                this.setContactDetailsFromUser();

                if (!this.user.orgs || this.user.orgs.size === 0) {
                    console.log('No orgs were assigned to user.');
                    return;
                }

                this.org = this.orgSiteSelectionService.getOrgSelectedValue();

                this.getClientInformation();

                if (!this.user.hasRole(RoleEnum.GeneralAccess, this.org.orgCode)) {
                    console.log(`Role [${RoleEnum.GeneralAccess}] is not assigned to user.`);
                    return;
                }

                const allowedSites = user.getAllowedSites(this.org.orgCode, RoleEnum.GeneralAccess);
                const orgWide = allowedSites?.length === 0;

                if (!orgWide && !Array.isArray(allowedSites)) {
                    console.log('No sites are assigned to user.');
                    return;
                }

                this.getSites(orgWide, Array.from(allowedSites));
            })
        );
    }

    private getClientInformation() {
        this.subscriptions.add(
            this.settingsService.getClientByOrg(this.org.orgCode).subscribe(response => {
                this.org.countryCode = response.countryCode;
                this.org.clientName = response.clientName;

                if (!this.defaultCountryCode && this.countrySettings) {
                    this.defaultCountryCode = this.countrySettings.filter(c => c.country === this.org.countryCode)[0].countryCode;
                }
            })
        );
    }

    private getSites(orgWide: boolean, allowedSites: number[]) {
        this.subscriptions.add(
            this.settingsService.getSitesByOrg(this.org.orgCode).subscribe(response => {
                this.sites = response
                    .map(item => new ClientSite(item.id, item.siteName))
                    .filter(client => orgWide || allowedSites.indexOf(client.siteId) !== -1)
                    .sort((a, b) => a.siteName.localeCompare(b.siteName));
                const defaultSiteId = this.org.defaultSiteId;
                if (!!defaultSiteId && this.sites.filter(site => site.siteId === defaultSiteId).length > 0) {
                    this.orderForm.controls.siteId.setValue(defaultSiteId);
                } else {
                    this.setSiteIfOnlyOne();
                }
            })
        );
    }

    private subscribeSiteIdChange() {
        this.subscriptions.add(
            this.form.siteId.valueChanges
                .pipe(switchMap(siteId => this.autocompleteService.getReferringDoctors(siteId)))
                .subscribe(referringDoctors => this.referringDoctorsSubject.next(referringDoctors))
        );
        this.subscriptions.add(
            this.form.siteId.valueChanges
                .pipe(switchMap(siteId => this.autocompleteService.getPatientLocations(siteId)))
                .subscribe(patientLocations => this.patientLocationsSubject.next(patientLocations))
        );
    }

    private getCountrySettings() {
        this.subscriptions.add(
            this.settingsService.getCountrySettings().subscribe(response => {
                this.countrySettings = response.map(item => new CountrySetting(item.country, item.region, item.countryCode));

                if (this.org?.countryCode && !this.defaultCountryCode) {
                    this.defaultCountryCode = this.countrySettings.filter(c => c.country === this.org.countryCode)[0].countryCode;
                }
            })
        );
    }

    private getCommonStudies() {
        this.subscriptions.add(
            this.settingsService.getCommonStudies().subscribe(response => {
                this.commonStudies = response;

                // Group common studies based on modality.
                this.studiesGroups = SettingsService.groupBy(this.commonStudies, commonStudy => commonStudy.modality);

                // The following should use SettingsService.getModalities() instead.
                if (this.studiesGroups) {
                    this.studiesGroups.forEach(value => {
                        value.sort((a, b) => a.description.localeCompare(b.description));
                    });

                    this.modalityList = [...this.studiesGroups.keys()].sort((a, b) => a.localeCompare(b));
                }
            })
        );
    }

    private setContactDetailsFromUser() {
        if (this.user?.username && this.orderForm && !this.orderForm.controls.callersName.value) {
            if (this.user.username.length > 64) {
                this.orderForm.controls.callersName.setValue(this.user.username.substring(0, 61) + '...');
            } else {
                this.orderForm.controls.callersName.setValue(this.user.username);
            }
        }

        if (this.user?.number && this.orderForm && !this.orderForm.controls.contactPhone.value) {
            this.orderForm.controls.contactPhone.setValue(this.user.number);
            if (this.user.extension) {
                this.orderForm.controls.contactPhoneExt.setValue(this.user.extension);
            }
        }
    }

    private setSiteIfOnlyOne() {
        if (this.sites && this.sites.length === 1) {
            this.orderForm.controls.siteId.setValue(this.sites[0].siteId);
            this.orderForm.controls.siteId.disable({ emitEvent: false });
        }
    }

    private initOrderFrom() {
        this.orderForm = this.formBuilder.group({
            siteId: [null, Validators.required],
            siteName: [null, Validators.nullValidator],

            patientFirstName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.NamePattern)]],
            patientLastName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.NamePattern)]],
            medicalRecordNumber: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.MrnPattern)]],
            patientBirthDate: [null, Validators.nullValidator],
            patientLocation: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.LocationNamePattern)]],

            urgency: [{ value: Constants.UrgencyStat, disabled: true }, Validators.required],
            protocol: [null, Validators.required],

            traumaStrokeSelection: [null, Validators.required],
            verbalRequired: [null, Validators.nullValidator],

            radiographerNotes: [null, Validators.nullValidator],

            priorSent: [false, Validators.nullValidator],

            callersName: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.NamePattern)]],
            contactPhone: [null, [Validators.required, Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(15)]],
            contactPhoneExt: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(this.MAX_SIZE_PHONE_EXTENSION)]],
            verbalDoctorReceivingReport: [null, [Validators.required, Validators.maxLength(64), Validators.pattern(Constants.NamePattern)]],
            referringDoctorPhoneNo: [null, [Validators.required, Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(15)]],
            referringDoctorExtension: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(this.MAX_SIZE_PHONE_EXTENSION)]],
            sameAsYourDetails: [null, Validators.nullValidator],
            radiographerName: [null, [Validators.maxLength(64), Validators.pattern(Constants.NamePattern)]],
            radiographerPhone: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(15)]],
            radiographerPhoneExt: [null, [Validators.pattern(this.PHONE_PATTERN), Validators.maxLength(this.MAX_SIZE_PHONE_EXTENSION)]],

            // The array for studies
            studies: this.formBuilder.array([]),

            // The array for priors
            priors: this.formBuilder.array([]),

            isToCreateAnotherOrder: [null, Validators.nullValidator]
        });

        this.setContactDetailsFromUser();
    }

    private newPriorForm(): UntypedFormGroup {
        return this.formBuilder.group({
            priorDate: [null, Validators.nullValidator],
            modality: [null, Validators.nullValidator],
            accessionNumber: [null, [Validators.maxLength(50), Validators.pattern(Constants.AccessionNumberPattern)]]
        });
    }

    private markAsTouched(form: UntypedFormGroup) {
        Object.keys(form.controls).forEach(field => {
            const control = form.get(field);

            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof UntypedFormArray) {
                const froms = control as UntypedFormArray;

                Object.keys(froms.controls).forEach(child => {
                    const childForm = froms.get(child) as UntypedFormGroup;

                    this.markAsTouched(childForm);
                });
            }
        });
    }

    private getOrderFromForm(): RemoteOrderRequest {
        const order = new RemoteOrderRequest({
            details: new RemoteOrderDetails({
                notes: ''
            }),
            items: [] as RemoteOrderItem[],
            priors: [] as RemoteOrderPrior[]
        });

        order.details.orgCode = this.org.orgCode;
        order.details.siteId = this.form.siteId.value;

        order.details.patientFirstName = this.form.patientFirstName.value;
        order.details.patientLastName = this.form.patientLastName.value;
        order.details.medicalRecordNumber = this.form.medicalRecordNumber.value;
        order.details.patientLocation = this.form.patientLocation.value;

        if (this.form.patientBirthDate.value) {
            order.details.patientBirthDate = DateTime.fromJSDate(this.form.patientBirthDate.value).toISODate();
        }

        order.details.protocol = this.form.protocol.value;
        if (this.form.protocol.value) {
            order.details.notes += (order.details?.notes?.length > 0 ? SEPARATOR_IN_NOTES : '') + 'Protocol: ' + order.details.protocol;
        }

        const selectedTraumaStrokeOption: UrgencyType | StrokeProtocol = this.orderForm.get('traumaStrokeSelection').value;
        let traumaProtocol = false;
        let strokeProtocolType: StrokeProtocol = StrokeProtocol.None;
        switch (selectedTraumaStrokeOption) {
            case UrgencyType.Urgent:
                break;
            case UrgencyType.Trauma:
                traumaProtocol = true;
                break;
            case UrgencyType.Stroke:
                strokeProtocolType = StrokeProtocol.Unspecified;
                break;
            case StrokeProtocol.SimpleStroke:
            case StrokeProtocol.Thrombectomy:
            case StrokeProtocol.Thrombolysis:
                strokeProtocolType = selectedTraumaStrokeOption as StrokeProtocol;
                break;

            case StrokeProtocol.None:
            case StrokeProtocol.Unspecified:
            case UrgencyType.Routine:
            case UrgencyType.TraumaAndStroke:
            default:
                console.error(`Unexpected dropdown selection: '${selectedTraumaStrokeOption}'`);
        }

        order.details.traumaProtocol = traumaProtocol;
        order.details.strokeProtocolType = strokeProtocolType;

        if (this.form.radiographerNotes.value) {
            // replace '\n' with '\r\n' (SEPARATOR_IN_NOTES) fo Windows text display
            order.details.radiographerNotes = this.form.radiographerNotes.value.replace(/\r/g, '').replace(/\n/g, SEPARATOR_IN_NOTES);
        }

        order.details.callersName = this.form.callersName.value;
        if (this.form.contactPhone.value) {
            order.details.contactPhone = this.defaultCountryCode + this.form.contactPhone.value;
            if (this.form.contactPhoneExt.value) {
                order.details.contactPhone += this.PHONE_EXTENSION_BRIEF + this.form.contactPhoneExt.value;
            }
        }

        order.details.verbalDoctorReceivingReport = this.form.verbalDoctorReceivingReport.value;
        if (this.form.referringDoctorPhoneNo.value) {
            order.details.referringDoctorCountryCode = this.referringDoctorCountryCode;
            order.details.referringDoctorPhoneNo = this.form.referringDoctorPhoneNo.value.toString();
            order.details.referringDoctorExtension = this.form.referringDoctorExtension.value?.toString();
        }
        if (this.form.sameAsYourDetails.value) {
            order.details.radiographerName = order.details.callersName;
            order.details.radiographerPhone = order.details.contactPhone;
        } else {
            order.details.radiographerName = this.form.radiographerName.value;
            if (this.form.radiographerPhone.value) {
                order.details.radiographerPhone = this.defaultCountryCode + this.form.radiographerPhone.value;
                if (this.form.radiographerPhoneExt.value) {
                    order.details.radiographerPhone += this.PHONE_EXTENSION_BRIEF + this.form.radiographerPhoneExt.value;
                }
            }

            // compare details between "Your details" and "Radiographer", add notes if they are different.
            if (
                (this.form.radiographerName.value || this.form.radiographerPhone.value) &&
                (order.details.radiographerName !== order.details.callersName ||
                    order.details.radiographerPhone !== order.details.contactPhone)
            ) {
                order.details.notes +=
                    (order.details?.notes?.length > 0 ? SEPARATOR_IN_NOTES : '') +
                    'Radiographer Details: Name:' +
                    (order.details.radiographerName ?? '') +
                    ', Number:' +
                    (order.details.radiographerPhone ?? '');
            }
        }

        // For Polytrauma and Thrombolysis, Urgency need be upgraded to SST from ST.
        order.details.urgency = this.elevateUrgency(order) ? Constants.UrgencySuperStat : Constants.UrgencyStat;

        this.studyForms.controls.forEach(child => {
            const childForm = child as UntypedFormGroup;

            const accessionNo =
                !childForm.controls.accessionNumber.value || childForm.controls.accessionNumber.value.length === 0
                    ? null
                    : childForm.controls.accessionNumber.value;
            if ((order.details.accessionNumber === null || order.details.accessionNumber === undefined) && accessionNo != null) {
                order.details.accessionNumber = accessionNo;
            }

            order.items.push(
                new RemoteOrderItem({
                    modality: childForm.controls.modality.value,
                    studyId: childForm.controls.studyId.value,
                    itemDescription: childForm.controls.studyDescription.value,
                    imageCount: childForm.controls.noOfImages.value,
                    accessionNumber: accessionNo
                })
            );
        });

        // Concat the studyIds to set commonStudiesIDs of Order
        order.details.commonStudiesIDs = '[' + order.items.map(item => item.studyId).join(',') + ']';

        // priors
        order.details.priorSent = this.form.priorSent.value;
        if (order.details.priorSent) {
            order.details.notes += (order.details?.notes?.length > 0 ? SEPARATOR_IN_NOTES : '') + 'Priors:';
            this.priorForms.controls.forEach(child => {
                const childForm = child as UntypedFormGroup;

                order.priors.push(
                    new RemoteOrderPrior({
                        priorDate: DateTime.fromJSDate(childForm.controls.priorDate.value).toISODate(),
                        modality: childForm.controls.modality.value,
                        accessionNumber:
                            !childForm.controls.accessionNumber.value || childForm.controls.accessionNumber.value.length === 0
                                ? null
                                : childForm.controls.accessionNumber.value
                    })
                );
                order.details.notes +=
                    (order.details?.notes?.length > 0 ? SEPARATOR_IN_NOTES : '') +
                    'Prior Date:' +
                    moment(childForm.controls.priorDate.value).format(Constants.DateFormat_DDMMYY) +
                    ', Modality:' +
                    childForm.controls.modality.value +
                    ', Accession Number:' +
                    (childForm.controls.accessionNumber.value ? childForm.controls.accessionNumber.value : '');
            });
        }

        return order;
    }

    private elevateUrgency(order: RemoteOrderRequest): boolean {
        if (order.details.traumaProtocol) {
            return true;
        }
        switch (order.details.strokeProtocolType) {
            case StrokeProtocol.Thrombectomy:
            case StrokeProtocol.Thrombolysis:
            case StrokeProtocol.Unspecified:
                return true;
            case StrokeProtocol.SimpleStroke:
            case StrokeProtocol.None:
            case null:
                return false;
        }
    }

    private pipeVerbalDoctorReceivingReportValueChange() {
        const verbalDoctorReceivingReportValueChanges$ = this.form.verbalDoctorReceivingReport.valueChanges.pipe(startWith(''));
        this.filteredReferringDoctors = combineLatest([
            verbalDoctorReceivingReportValueChanges$,
            this.referringDoctorsSubject.asObservable()
        ]).pipe(map(([doctorName, referringDoctors]) => this.filterReferringDoctor(doctorName, referringDoctors)));

        this.filteredReferringDoctorPhoneNo = combineLatest([
            verbalDoctorReceivingReportValueChanges$,
            this.referringDoctorsSubject.asObservable()
        ]).pipe(map(([doctorName, referringDoctors]) => this.filterReferringDoctorPhoneNo(doctorName, referringDoctors)));
    }

    private pipeReferringDoctorExtensionValueChanges() {
        const verbalDoctorReceivingReportValueChanges$ = this.form.verbalDoctorReceivingReport.valueChanges.pipe(startWith(''));
        const referringDoctorPhoneNoValueChanges$ = this.form.referringDoctorPhoneNo.valueChanges.pipe(startWith(''));
        this.filteredReferringDoctorExtension = combineLatest([
            verbalDoctorReceivingReportValueChanges$,
            referringDoctorPhoneNoValueChanges$,
            this.referringDoctorsSubject.asObservable()
        ]).pipe(
            map(([doctorName, phoneNo, referringDoctors]) => this.filterReferringDoctorExtension(doctorName, phoneNo, referringDoctors))
        );
    }

    private pipePatientLocationValueChanges() {
        const patientLocationValueChanges$ = this.form.patientLocation.valueChanges.pipe(startWith(''));
        this.filteredPatientLocation = combineLatest([patientLocationValueChanges$, this.patientLocationsSubject.asObservable()]).pipe(
            map(([value, patientLocations]) => this.filterPatientLocation(value, patientLocations))
        );
    }

    private filterReferringDoctor(value: string, referringDoctors: ReferringDoctorDTO[]): string[] {
        const filterValue = value?.toLowerCase() ?? '';
        let result: string[];

        if (referringDoctors && filterValue && filterValue.length >= 3) {
            result = referringDoctors
                .filter(option => option.name.toLowerCase().includes(filterValue)) // Filter by keyword
                .map(option => option.name) // Only return name
                .reduce((arr, v) => {
                    // Reduce duplicate
                    if (arr.indexOf(v) < 0) {
                        arr.push(v);
                    }
                    return arr;
                }, [])
                .sort((a, b) => a.localeCompare(b)); // sort alphabetically
        } else {
            result = [];
        }

        return result;
    }

    private filterReferringDoctorPhoneNo(doctorName: string, referringDoctors: ReferringDoctorDTO[]): string[] {
        doctorName = doctorName?.toLowerCase() ?? '';
        this._verbalPhoneCountryCode = null; // Revert to defaultCountryCode
        if (referringDoctors) {
            const doctorDTOs = referringDoctors.filter(dto => dto.name.toLowerCase() === doctorName);
            return doctorDTOs
                .map(dto => dto.phone)
                .reduce((arr, v) => {
                    // Reduce duplicate
                    if (arr.indexOf(v) < 0) {
                        arr.push(v);
                    }
                    return arr;
                }, [])
                .sort((a, b) => a.localeCompare(b)); // sort alphabetically
        } else {
            return [];
        }
    }

    private filterReferringDoctorExtension(doctorName: string, phoneNo: string, referringDoctors: ReferringDoctorDTO[]): string[] {
        doctorName = doctorName?.toLowerCase() ?? '';
        if (referringDoctors) {
            const doctorDTOs = referringDoctors.filter(
                dto => dto.name.toLowerCase() === doctorName && dto.phone === phoneNo && dto.extension
            );
            return doctorDTOs // Not filtering by keyword as options are too little
                .map(dto => dto.extension)
                .reduce((arr, v) => {
                    // Reduce duplicate
                    if (arr.indexOf(v) < 0) {
                        arr.push(v);
                    }
                    return arr;
                }, [])
                .sort((a, b) => a.localeCompare(b)); // sort alphabetically
        } else {
            return [];
        }
    }

    private filterPatientLocation(value: string, patientLocations: PatientLocationDTO[]): string[] {
        const patientLocation = value?.toLowerCase() ?? '';
        if (patientLocations) {
            const patientLocationDTOs = patientLocations.filter(dto => dto.location.toLowerCase().includes(patientLocation));
            return patientLocationDTOs.map(dto => dto.location).sort((a, b) => a.localeCompare(b)); // sort alphabetically
        } else {
            return [];
        }
    }

    selectedDoctor(event) {
        if (!this.referringDoctorsSubject.value) {
            return;
        }
        const doctorName = (event.option.value as string).toLowerCase();
        const matchingDoctorDTO = this.referringDoctorsSubject.value.find(dto => dto.name.toLowerCase() === doctorName);
        this.form.referringDoctorPhoneNo.setValue(matchingDoctorDTO.phone);
        this.form.referringDoctorExtension.setValue(matchingDoctorDTO.extension);
        this._verbalPhoneCountryCode = matchingDoctorDTO.countryCode;
    }

    selectReferringDoctorPhoneNo(event) {
        if (!this.referringDoctorsSubject.value) {
            return;
        }
        const doctorName = this.form.verbalDoctorReceivingReport.value?.toLowerCase() ?? '';
        const phoneNo = event.option.value as string;
        const matchingDoctorDTO = this.referringDoctorsSubject.value.find(
            dto => dto.name.toLowerCase() === doctorName && dto.phone === phoneNo
        );
        if (matchingDoctorDTO) {
            this.form.referringDoctorExtension.setValue(matchingDoctorDTO.extension);
            this._verbalPhoneCountryCode = matchingDoctorDTO.countryCode;
            this.form.referringDoctorExtension.setValue(matchingDoctorDTO.extension);
        }
    }

    private areStrokeQueryTypesEnabled(): Observable<boolean> {
        return this.orgSiteSelectionService
            .getOrgSelected$()
            .pipe(map(org => this.featuresService.isFeatureEnabled('strokeQueryType', org.countryCode)));
    }

    private initUrgencyDropdownOptions(): void {
        const strokeQueryTypesEnabled = this.areStrokeQueryTypesEnabled().pipe(
            map(strokeQueryTypeEnabledForCountry => strokeQueryTypeEnabledForCountry)
        );

        this.subscriptions.add(
            strokeQueryTypesEnabled.subscribe(strokeQueryTypesEnabled => {
                // Make a copy of the top level elements so we do not edit the original.
                const urgencyDropdownOptions: DropdownOption<UrgencyType | StrokeProtocol>[] = Constants.UrgencyDropdownOptions.map(
                    option => ({ ...option })
                );
                if (strokeQueryTypesEnabled) {
                    const strokeOption = urgencyDropdownOptions.find(option => option.value === UrgencyType.Stroke);
                    strokeOption.subValues = Constants.StrokeDropdownOptions;
                }
                this.urgencyDropdownOptions.next(urgencyDropdownOptions);
            })
        );
    }
}
